import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { useActiveWindowInterval } from 'components/hooks';
import { IS_DEBUG_ENVIRONMENT, SHOW_DEBUG_PANELS } from 'config';
import { waitAsync } from 'lib/promiseTools';

import QrCodeScannerDevInput from './QrCodeScannerDevInput';
import { getImageDataUsingCanvas } from './qrCodeScannerUtils';
import { getWebCamStream } from './videoUtils';

const Wrap = styled.div`
  display: flex;
  flex-direction: center;
  justify-content: center;

  & > video {
    position: absolute;
    top: 0;
    bottom: 0;
    height: 100%;
  }
`;

export default function QrCodeScanner({
  successCallback,
  errorCallback,
}: {
  successCallback: (text: string) => void;
  errorCallback: (errorMessage: string) => void;
}) {
  const videoStreamRef = useRef<Promise<MediaStream> | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const processingImage = useRef(false);

  const pauseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
  };

  const sendStreamToVideoElement = (stream: MediaStream) => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play();
      };
    } else {
      console.error('[QrCodeScanner] Video element not ready');
    }
  };

  const startVideoStream = useCallback(() => {
    console.log('[QrCodeScanner] Getting media permission...');
    videoStreamRef.current = getWebCamStream();
    videoStreamRef.current.then(sendStreamToVideoElement).catch(error => {
      console.warn(error);
      errorCallback(error?.message || 'Error');
    });
  }, [errorCallback]);

  const stopVideoStream = useCallback(() => {
    if (videoStreamRef.current !== null) {
      pauseVideo();
      videoStreamRef.current.then(stream => {
        stream.getTracks().forEach(track => track.stop());
      });
    }
  }, []);

  useEffect(() => {
    startVideoStream();
    return () => {
      stopVideoStream();
    };
  }, [startVideoStream, stopVideoStream]);

  function getImageFromVideoFrame() {
    const video = videoRef.current;
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
      console.debug('[QrCodeScanner] Not enough video data');
      return;
    }
    const imageData = getImageDataUsingCanvas({
      source: video,
      width: video.videoWidth,
      height: video.videoHeight,
    });
    console.debug('[QrCodeScanner] Returning image frame from video');
    return imageData;
  }

  /** https://github.com/cozmo/jsQR/blob/master/docs/index.html */
  const handleParseImage = async (imageData: ImageData) => {
    const jsQR = (await import('jsqr')).default;
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });
    await waitAsync();
    if (!code?.data) {
      // No QR data parsed (Expected until QR code is readable)
      return;
    }
    console.log('[QrCodeScanner] Found QR code');

    stopVideoStream();

    successCallback(code.data);
  };

  useActiveWindowInterval(() => {
    if (videoRef?.current?.paused) {
      return;
    }
    if (!processingImage.current) {
      // Make sure we don't trigger processing multiple times
      processingImage.current = true;
      const imageData = getImageFromVideoFrame();
      if (!imageData) {
        console.log('[QrCodeScanner] No image data');
        return;
      }
      handleParseImage(imageData).finally(() => {
        processingImage.current = false;
      });
    }
  }, 300); // ~3 frames/second

  // const handleRetry = async () => {
  //   stopVideoStream();
  //   setErrorMessage('');
  //   await waitAsync(500);
  //   startVideoStream();
  // };

  return (
    <Wrap>
      <video
        ref={videoRef}
        playsInline
        muted
        style={{ maxWidth: '100%', maxHeight: '70vh' }}
      />
      {IS_DEBUG_ENVIRONMENT && SHOW_DEBUG_PANELS && (
        <QrCodeScannerDevInput onImageData={handleParseImage} />
      )}
    </Wrap>
  );
}
